import * as THREE from 'three';

/**
 * Fully procedural audio — no samples ship with the build. Gunshots are a
 * noise burst through a resonant lowpass plus a body thump, then convolved
 * with a synthesised outdoor tail for the "crack ... slap" of urban gunfire.
 */
export class AudioEngine {
  constructor(camera) {
    this.camera = camera;
    this.ready = false;
    this.listenerPos = new THREE.Vector3();
  }

  init() {
    if (this.ready) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    this.ctx = new Ctx();
    this.ready = true;

    this.master = this.ctx.createGain();
    this.master.gain.value = 0.55;

    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -8;
    this.limiter.knee.value = 6;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.002;
    this.limiter.release.value = 0.18;

    this.master.connect(this.limiter).connect(this.ctx.destination);

    // Reverb send.
    this.reverb = this.ctx.createConvolver();
    this.reverb.buffer = this._makeIR(1.8, 2.6);
    this.reverbGain = this.ctx.createGain();
    this.reverbGain.gain.value = 0.34;
    this.reverb.connect(this.reverbGain).connect(this.master);

    this.noiseBuffer = this._makeNoise(2);
    this._ambience();
  }

  _makeNoise(seconds) {
    const len = this.ctx.sampleRate * seconds;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }

  _makeIR(seconds, decay) {
    const rate = this.ctx.sampleRate;
    const len = rate * seconds | 0;
    const buf = this.ctx.createBuffer(2, len, rate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        const t = i / len;
        // Sparse early reflections then an exponential diffuse tail.
        const early = (i % (rate * 0.023 | 0) < 3) ? (Math.random() * 2 - 1) * 0.5 : 0;
        d[i] = ((Math.random() * 2 - 1) * 0.55 + early) * Math.pow(1 - t, decay);
      }
    }
    return buf;
  }

  _noiseSource() {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    src.playbackRate.value = 0.8 + Math.random() * 0.4;
    return src;
  }

  /** 3D panner chain: returns the node you connect a source into. */
  _spatial(pos, { refDistance = 6, maxDistance = 160, rolloff = 1.4 } = {}) {
    const panner = this.ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = refDistance;
    panner.maxDistance = maxDistance;
    panner.rolloffFactor = rolloff;
    if (pos) panner.positionX.value = pos.x, panner.positionY.value = pos.y, panner.positionZ.value = pos.z;
    panner.connect(this.master);
    const send = this.ctx.createGain();
    send.gain.value = 0.5;
    panner.connect(send).connect(this.reverb);
    return panner;
  }

  /**
   * Per-weapon voices.
   *
   * A gunshot is three layers: the supersonic CRACK (bright noise, band-swept
   * down), the BODY (low sine, the chest thump), and the mechanical ACTION.
   * What separates a 9mm from a 12-gauge is the ratio between them and how fast
   * each decays — a pistol is nearly all crack with a short tail, a shotgun is
   * nearly all body with a long one, and a launcher has no crack at all because
   * nothing supersonic leaves the tube.
   */
  static VOICES = {
    rifle:    { cf0: 2600, cf1: 700,  cq: 0.9, cg: 0.85, cd: 0.16,
                bf0: 160,  bf1: 48,   bg: 0.55, bd: 0.18, act: 0.14, actHz: 3400 },
    smg:      { cf0: 3300, cf1: 950,  cq: 1.1, cg: 0.72, cd: 0.11,
                bf0: 190,  bf1: 70,   bg: 0.34, bd: 0.11, act: 0.18, actHz: 4200 },
    dmr:      { cf0: 2000, cf1: 420,  cq: 0.7, cg: 1.0,  cd: 0.30,
                bf0: 120,  bf1: 34,   bg: 0.78, bd: 0.34, act: 0.12, actHz: 3000,
                tail: 0.55 },
    pistol:   { cf0: 2900, cf1: 820,  cq: 1.3, cg: 0.62, cd: 0.10,
                bf0: 150,  bf1: 58,   bg: 0.30, bd: 0.10, act: 0.16, actHz: 3800 },
    shotgun:  { cf0: 1250, cf1: 260,  cq: 0.5, cg: 0.95, cd: 0.34,
                bf0: 95,   bf1: 28,   bg: 0.95, bd: 0.40, act: 0.10, actHz: 2600,
                tail: 0.7 },
    launcher: { cf0: 700,  cf1: 180,  cq: 0.4, cg: 0.85, cd: 0.75,
                bf0: 78,   bf1: 22,   bg: 1.0,  bd: 0.85, act: 0.0,  actHz: 2000,
                tail: 0.9, whoosh: true },
  };

  gunshot(pos, { close = true, gain = 1, voice = 'rifle' } = {}) {
    if (!this.ready) return;
    const V = AudioEngine.VOICES[voice] ?? AudioEngine.VOICES.rifle;
    const t = this.ctx.currentTime;
    const dest = close ? this.master : this._spatial(pos);

    // Crack: bright noise transient, band-swept downward.
    const crack = this._noiseSource();
    const crackFilter = this.ctx.createBiquadFilter();
    crackFilter.type = 'bandpass';
    crackFilter.frequency.setValueAtTime(V.cf0, t);
    crackFilter.frequency.exponentialRampToValueAtTime(V.cf1, t + V.cd * 0.6);
    crackFilter.Q.value = V.cq;
    const crackGain = this.ctx.createGain();
    crackGain.gain.setValueAtTime(0.0001, t);
    crackGain.gain.exponentialRampToValueAtTime(V.cg * gain, t + (V.whoosh ? 0.02 : 0.002));
    crackGain.gain.exponentialRampToValueAtTime(0.0001, t + V.cd);
    crack.connect(crackFilter).connect(crackGain).connect(dest);
    if (close) crackGain.connect(this.reverb);
    crack.start(t);
    crack.stop(t + V.cd + 0.05);

    // Body: low sine thump gives the shot its weight.
    const body = this.ctx.createOscillator();
    body.type = 'sine';
    body.frequency.setValueAtTime(V.bf0, t);
    body.frequency.exponentialRampToValueAtTime(V.bf1, t + V.bd * 0.6);
    const bodyGain = this.ctx.createGain();
    bodyGain.gain.setValueAtTime(0.0001, t);
    bodyGain.gain.exponentialRampToValueAtTime(V.bg * gain, t + 0.004);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, t + V.bd);
    body.connect(bodyGain).connect(dest);
    body.start(t);
    body.stop(t + V.bd + 0.05);

    // Big-bore weapons get a low-passed tail: the report coming back off the
    // buildings, which is most of why a shotgun sounds large.
    if (V.tail) {
      const tail = this._noiseSource();
      const tf = this.ctx.createBiquadFilter();
      tf.type = 'lowpass';
      tf.frequency.setValueAtTime(900, t);
      tf.frequency.exponentialRampToValueAtTime(180, t + V.tail);
      const tg = this.ctx.createGain();
      tg.gain.setValueAtTime(0.0001, t + 0.01);
      tg.gain.exponentialRampToValueAtTime(0.30 * gain, t + 0.04);
      tg.gain.exponentialRampToValueAtTime(0.0001, t + V.tail);
      tail.connect(tf).connect(tg).connect(dest);
      tg.connect(this.reverb);
      tail.start(t);
      tail.stop(t + V.tail + 0.05);
    }

    // Mechanical action. Launchers have none.
    if (V.act > 0) {
      const click = this._noiseSource();
      const clickF = this.ctx.createBiquadFilter();
      clickF.type = 'highpass';
      clickF.frequency.value = V.actHz;
      const clickG = this.ctx.createGain();
      clickG.gain.setValueAtTime(V.act * gain, t + 0.012);
      clickG.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
      click.connect(clickF).connect(clickG).connect(dest);
      click.start(t + 0.012);
      click.stop(t + 0.09);
    }
  }

  /**
   * Bomb beep. `urgency` 0..1 raises pitch and gain as the fuse runs down, so
   * the sound alone tells you how long is left. Audible much further than a
   * footstep — the whole team needs to hear a planted bomb.
   */
  bombBeep(pos, urgency = 0) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const dest = this._spatial(pos, { refDistance: 12, maxDistance: 400, rolloff: 0.8 });
    const o = this.ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = 2350 + urgency * 900;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = o.frequency.value;
    f.Q.value = 8;
    const g = this.ctx.createGain();
    const peak = 0.05 + urgency * 0.09;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.055);
    o.connect(f).connect(g).connect(dest);
    o.start(t);
    o.stop(t + 0.07);
  }

  /** Planting: a ratchet of mechanical clicks. */
  bombPlant(pos) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const dest = this._spatial(pos, { refDistance: 6, maxDistance: 90, rolloff: 1.5 });
    for (let i = 0; i < 5; i++) {
      const src = this._noiseSource();
      const f = this.ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 1800 + i * 260;
      f.Q.value = 5;
      const g = this.ctx.createGain();
      const at = t + i * 0.055;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.10, at + 0.002);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
      src.connect(f).connect(g).connect(dest);
      src.start(at);
      src.stop(at + 0.07);
    }
  }

  /** Defused: two descending clicks and a short confidence tone. */
  bombDefused(pos) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const dest = this._spatial(pos, { refDistance: 8, maxDistance: 200, rolloff: 1.0 });
    for (const [i, hz] of [1500, 900].entries()) {
      const o = this.ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = hz;
      const g = this.ctx.createGain();
      const at = t + i * 0.09;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.09, at + 0.003);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.07);
      o.connect(g).connect(dest);
      o.start(at); o.stop(at + 0.09);
    }
    const o = this.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(420, t + 0.2);
    o.frequency.exponentialRampToValueAtTime(640, t + 0.42);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + 0.2);
    g.gain.exponentialRampToValueAtTime(0.10, t + 0.24);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
    o.connect(g).connect(dest);
    o.start(t + 0.2); o.stop(t + 0.6);
  }

  /** Knife swing: a short filtered noise whoosh, no transient. */
  melee(pos) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const src = this._noiseSource();
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.setValueAtTime(900, t);
    f.frequency.exponentialRampToValueAtTime(2600, t + 0.09);
    f.Q.value = 1.4;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.035);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    src.connect(f).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 0.2);
  }

  /** Blade landing: a wet thud plus a brief metallic ring. */
  meleeHit(pos) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const dest = this._spatial(pos, { refDistance: 3, maxDistance: 40, rolloff: 2 });
    const thud = this.ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(190, t);
    thud.frequency.exponentialRampToValueAtTime(58, t + 0.09);
    const tg = this.ctx.createGain();
    tg.gain.setValueAtTime(0.0001, t);
    tg.gain.exponentialRampToValueAtTime(0.42, t + 0.004);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
    thud.connect(tg).connect(dest);
    thud.start(t); thud.stop(t + 0.18);

    const ring = this.ctx.createOscillator();
    ring.type = 'triangle';
    ring.frequency.value = 3100;
    const rg = this.ctx.createGain();
    rg.gain.setValueAtTime(0.0001, t);
    rg.gain.exponentialRampToValueAtTime(0.07, t + 0.003);
    rg.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
    ring.connect(rg).connect(dest);
    ring.start(t); ring.stop(t + 0.13);
  }

  impact(pos, kind = 'concrete') {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const dest = this._spatial(pos, { refDistance: 3, maxDistance: 60, rolloff: 2 });
    const src = this._noiseSource();
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = kind === 'metal' ? 3200 : 1100;
    f.Q.value = kind === 'metal' ? 6 : 1.4;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(kind === 'metal' ? 0.34 : 0.26, t + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, t + (kind === 'metal' ? 0.34 : 0.11));
    src.connect(f).connect(g).connect(dest);
    src.start(t);
    src.stop(t + 0.4);
  }

  footstep(pos, intensity = 1) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const src = this._noiseSource();
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 900 + Math.random() * 500;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.10 * intensity, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
    src.connect(f).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 0.14);
  }

  shellDrop(pos, speed) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const dest = this._spatial(pos, { refDistance: 2, maxDistance: 20, rolloff: 2.5 });
    for (let i = 0; i < 2; i++) {
      const o = this.ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = 2400 + Math.random() * 2600;
      const g = this.ctx.createGain();
      const at = t + i * 0.045;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(Math.min(0.09, 0.03 * speed), at + 0.002);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.09);
      o.connect(g).connect(dest);
      o.start(at);
      o.stop(at + 0.12);
    }
  }

  reload(stage) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const freq = { out: 900, in: 1400, bolt: 2200 }[stage] ?? 1200;
    const o = this.ctx.createOscillator();
    o.type = 'square';
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.4, t + 0.05);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.12, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = 3;
    o.connect(f).connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.12);
  }

  hitmarker(kill = false) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(kill ? 1500 : 1180, t);
    o.frequency.exponentialRampToValueAtTime(kill ? 780 : 940, t + 0.07);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.1);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 0.12);
  }

  /* ------------------------------------------------------ killstreaks --- */

  /** Two-tone chime: a reward, so it sits above the mix and resolves upward. */
  streakReady() {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    for (const [i, f] of [740, 1108].entries()) {
      const o = this.ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(f, t + i * 0.09);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t + i * 0.09);
      g.gain.exponentialRampToValueAtTime(0.14, t + i * 0.09 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.09 + 0.34);
      o.connect(g).connect(this.master);
      o.start(t + i * 0.09);
      o.stop(t + i * 0.09 + 0.36);
    }
  }

  /** Radar sweep: a filtered blip with a long tail, twice. */
  uav() {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    for (let i = 0; i < 2; i++) {
      const o = this.ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(1320, t + i * 0.16);
      o.frequency.exponentialRampToValueAtTime(660, t + i * 0.16 + 0.18);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t + i * 0.16);
      g.gain.exponentialRampToValueAtTime(0.1, t + i * 0.16 + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.16 + 0.3);
      o.connect(g).connect(this.master);
      o.start(t + i * 0.16);
      o.stop(t + i * 0.16 + 0.32);
    }
  }

  /** Jet pass: noise swept through a moving band, so it reads as approaching. */
  strikeInbound() {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const src = this._noiseSource();
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.Q.value = 1.6;
    bp.frequency.setValueAtTime(320, t);
    bp.frequency.exponentialRampToValueAtTime(2600, t + 2.1);
    bp.frequency.exponentialRampToValueAtTime(420, t + 3.2);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.16, t + 1.6);
    g.gain.linearRampToValueAtTime(0.22, t + 2.2);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 3.3);
    src.connect(bp).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 3.4);
  }

  /** Rotor wash on arrival — low thump train, not a loop. */
  heliIn() {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    for (let i = 0; i < 18; i++) {
      const at = t + i * 0.115;
      const o = this.ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(58, at);
      o.frequency.exponentialRampToValueAtTime(34, at + 0.09);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.13, at + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.1);
      o.connect(g).connect(this.master);
      o.start(at);
      o.stop(at + 0.12);
    }
  }

  /** Chain gun, heard from below and far off: short, dry, distance-filtered. */
  heliGun(pos) {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const src = this._noiseSource();
    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900 + Math.random() * 260;
    bp.Q.value = 0.8;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    src.connect(bp).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 0.1);
  }

  /** Nothing to spend: a flat click, so the key never feels unresponsive. */
  dryFire() {
    if (!this.ready) return;
    const t = this.ctx.currentTime;
    const src = this._noiseSource();
    const hp = this.ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2400;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    src.connect(hp).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + 0.06);
  }

  _ambience() {
    // Low wind bed — filtered noise with a slowly wandering cutoff.
    const src = this._noiseSource();
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 340;
    const g = this.ctx.createGain();
    g.gain.value = 0.05;
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 180;
    lfo.connect(lfoGain).connect(f.frequency);
    src.connect(f).connect(g).connect(this.master);
    src.start();
    lfo.start();
  }

  /** Keep the WebAudio listener glued to the camera. */
  update(camera) {
    if (!this.ready) return;
    const l = this.ctx.listener;
    const p = camera.position;
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
    if (l.positionX) {
      l.positionX.value = p.x; l.positionY.value = p.y; l.positionZ.value = p.z;
      l.forwardX.value = fwd.x; l.forwardY.value = fwd.y; l.forwardZ.value = fwd.z;
      l.upX.value = up.x; l.upY.value = up.y; l.upZ.value = up.z;
    } else {
      l.setPosition(p.x, p.y, p.z);
      l.setOrientation(fwd.x, fwd.y, fwd.z, up.x, up.y, up.z);
    }
  }

  playAt(kind, pos) {
    if (kind === 'enemyShot') this.gunshot(pos, { close: false, gain: 0.8, voice: 'rifle' });
  }
}
